import { createTheme } from '@mui/material/styles';
import {
  outlinedInputClasses,
  inputClasses,
  inputLabelClasses,
  accordionClasses,
  accordionSummaryClasses,
  tabClasses,
  toggleButtonClasses,
  selectClasses,
  dialogTitleClasses,
} from '@mui/material';

export const Typography = {
  fontFamily: 'Inter, sans-serif',
  htmlFontSize: 10, // 10px base for rem calculations
  fontSize: 16,
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    bold: 600, // This is typically 700. Should we change it?
  },
};

export const Colors = {
  // Base
  white: '#ffffff',
  black: '#000000',
  gray: '#808080',
  red: '#ff0000',
  green: '#00ff00',
  blue: '#0000ff',
  // Black/Gray shades
  codGray: '#191919',
  dustyGray: '#959595',
  athensGray: '#f3f6f8',
  dustGray: '#999999',
  haze: '#edf0f3',
  silver: '#bfbfbf',
  mercury: '#d7dde3',
  // Red shades
  paarl: '#a65628',
  cancan: '#da96a6',
  cadillac: '#af4a74',
  christine: '#f36f13',
  thunderbird: '#c13017',
  alizarinCrimson: '#db544a',
  buccaneer: '#5e2727',
  // Green shades
  jaggeIce: '#d0eedd',
  surfCrest: '#c8e4c8',
  mountainMeadow: '#20c161',
  olivine: '#23df78',
  eucalyptus: '#299d5e',
  finlandia: '#5a6b5a',
  lunarGreen: '#373a37',
  // Blue shades
  tiber: '#062c3f',
  havelockBlue: '#6383dd',
  pacificBlue: '#0b94bc',
  tarawera: '#063951',
  // Yellow shades
  hokeyPok: '#d8b031',
  goldenDream: '#ebcb39',
  peachYellow: '#faeaa3', // 'Sandwisp' on Zeplin
  // Not on Zeplin Palette
  primaryLight: '#62d390',
  primaryDark: '#179048',
  secondaryLight: '#d2f3df',
  secondaryDark: '#a6e6c0',
  alphaWhite: '#fbfbfb',
  rgbaGray87: 'rgba(0, 0, 0, 0.87)',
  rgbaGray12: 'rgba(0, 0, 0, 0.12)',
  affair: '#984ea3',
  bostonBlue: '#377eb8',
  aquaSqueeze: '#e8f6f4',
  goldenFizz: '#ffff33',
  goldenSand: '#f2d974',
  geyser: '#d6dde4',
  bamboo: '#db6400',
  flushOrange: '#ff7f00',
  fern: '#61b15a',
  persianPink: '#f781bf',
  wildSand: '#f6f6f6',
  stJohn: '#285469',
  lunarGreenAlpha10: '#d6e2e1',
  apple: '#43b542',
  edward: '#abaeae',
  melanie: '#d5abbc',
  rollingStone: '#737578',
  silverSand: '#b4bbbd',
  lynch: '#647f99',
  flameaPea: '#e02020',
  nandor: '#515e51',
};

// TODO: Complete entire color scales with _consistent_ shades.
const ColorScales = {
  primary: {
    100: '#E1F3E1',
    200: '#D2F3DF',
    alt200: Colors.jaggeIce, // Used with selected sidebar links. May just be an opacity variation.
    300: '#A6E6C0',
    alt300: Colors.surfCrest, // Used with inactive tabs text and site system overview labels.
    400: Colors.primaryLight,
    500: Colors.mountainMeadow,
    600: Colors.primaryDark,
    // No 700-900 defined
  },
  // Using default MUI error, success, warning, and info colors.
  error: {
    300: '#ef5350',
    // No 400 defined
    500: '#d32f2f',
    // No 600 defined
    700: '#c62828',
  },
  success: {
    300: '#4caf50',
    // No 400 defined
    500: '#2e7d32',
    // No 600 defined
    700: '#1b5e20',
  },
  warning: {
    300: '#ff9800',
    // No 400 defined
    500: '#ed6c02',
    // No 600 defined
    700: '#e65100',
  },
  info: {
    300: '#03a9f4',
    // No 400 defined
    500: '#0288d1',
    // No 600 defined
    700: '#01579b',
  },
  neutral: {
    100: Colors.athensGray,
    200: Colors.haze,
    300: '#E4E9EF',
    400: Colors.mercury,
    500: Colors.silverSand,
    600: Colors.dustGray,
    // No 700 defined
    // No 800 defined

    900: Colors.lunarGreen,
  },
};

export const Palette = {
  primary: {
    ...ColorScales.primary,
    lighter: ColorScales.primary[300],
    light: ColorScales.primary[400],
    main: ColorScales.primary[500],
    dark: ColorScales.primary[600],
    contrastText: Colors.white,
  },
  error: {
    ...ColorScales.error,
    light: ColorScales.error[300],
    main: ColorScales.error[500],
    dark: ColorScales.error[700],
  },
  success: {
    ...ColorScales.success,
    light: ColorScales.success[300],
    main: ColorScales.success[500],
    dark: ColorScales.success[700],
  },
  warning: {
    ...ColorScales.warning,
    light: ColorScales.warning[300],
    main: ColorScales.warning[500],
    dark: ColorScales.warning[700],
  },
  info: {
    ...ColorScales.info,
    light: ColorScales.info[400],
    main: ColorScales.info[500],
    dark: ColorScales.info[600],
  },
  // TODO: Standardize usage of these colors throughout the app.
  /**   Copy neutral color scales to {@link import('@mui/material').PaletteOptions.grey} along with additional shades it requires. */
  neutral: {
    ...ColorScales.neutral,
    // Ideally, this should be more of a middle ground for neutral colors, like 500.
    main: ColorScales.neutral[900],
  },
  text: {
    primary: ColorScales.neutral[900],
    secondary: ColorScales.neutral[600],
    muted: ColorScales.neutral[600],
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  border: {
    main: ColorScales.neutral[400],
    disabled: ColorScales.neutral[500],
    hover: ColorScales.neutral[500],
  },
  divider: ColorScales.neutral[400], // Should match border.main
  background: {
    default: Colors.white,
    paper: Colors.white,
    body: ColorScales.neutral[200],
    muted: ColorScales.neutral[100],
    mutedHover: ColorScales.neutral[300],
    disabled: ColorScales.neutral[400],
  },
  dashboard: {
    light: {
      shellBackground: Colors.white,
      shellBorder: ColorScales.neutral[400],
      cardBackground: Colors.white,
      cardHeaderBackground: '#f8fafc',
      text: '#0f172a',
      textMuted: '#64748b',
      textSoft: '#334155',
      title: '#0f766e',
      iconMuted: '#64748b',
    },
    dark: {
      shellBackground: '#0b0f16',
      shellBorder: '#20293a',
      cardBackground: '#0f1217',
      cardHeaderBackground: '#111317',
      text: '#e2e8f0',
      textMuted: '#b8c5d6',
      textSoft: '#d1d5db',
      title: '#20b486',
      iconMuted: '#b8c5d6',
    },
    overlayShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.2)',
    weather: {
      cardText: Colors.white,
      lightGradientStart: '#7aa2ff',
      lightGradientEnd: '#9cc7ff',
      darkGradientStart: '#1f2937',
      darkGradientEnd: '#0f172a',
      shadow: '0 20px 40px rgba(0,0,0,0.25)',
      shadowHover: '0 28px 60px rgba(0,0,0,0.3)',
      details: {
        dayCardActiveBorder: '#1976d2',
        dayCardIdleBorder: 'rgba(0,0,0,0.12)',
        dayCardActiveBackground: 'rgba(25,118,210,0.08)',
        hourCellBackground: 'rgba(0,0,0,0.04)',
      },
    },
    charts: {
      devicesByTypePalette: [
        '#10B981',
        '#60A5FA',
        '#F59E0B',
        '#EF4444',
        '#8B5CF6',
        '#34D399',
        '#F472B6',
        '#F87171',
        '#2DD4BF',
        '#A78BFA',
        '#FBBF24',
        '#93C5FD',
      ],
      donutBarColors: [
        { name: 'Solar', color: '#4192b8' },
        { name: 'ESS', color: '#db6400' },
        { name: 'Engines', color: '#f2d974' },
        { name: 'Utility', color: '#61b15a' },
      ],
      stackedBarColors: [
        { name: 'EV Charging', color: '#FF0000' },
        { name: 'Facility Use', color: '#0FF000' },
        { name: 'R.Engines', color: '#00FF00' },
        { name: 'Solar', color: '#000FF0' },
        { name: 'Microturbine', color: '#0000FF' },
      ],
      events: {
        severity: {
          Fault: '#ef4444',
          Alarm: '#f97316',
          Warning: '#eab308',
        },
        fallback: ['#3b82f6', '#22c55e', '#8b5cf6', '#06b6d4', '#ec4899'],
        axisLight: '#475569',
        axisDark: '#cbd5e1',
        gridLight: '#cbd5e1',
        gridDark: '#334155',
        tooltipLightBackground: '#ffffff',
        tooltipDarkBackground: '#111827',
        tooltipLightBorder: '#e5e7eb',
        tooltipDarkBorder: '#1f2937',
        tooltipLightText: '#111827',
        tooltipDarkText: '#e5e7eb',
        tooltipDarkBackgroundElevated: '#0b1220',
        tooltipDarkBorderElevated: '#1e293b',
        tooltipDarkLabelText: '#f8fafc',
        tooltipDarkItemText: '#e2e8f0',
        tooltipCursorDark: 'rgba(15, 23, 42, 0.5)',
        tooltipCursorLight: 'rgba(148, 163, 184, 0.2)',
      },
      devicesByType: {
        lineGridDark: '#64748b',
        lineGridLight: '#cbd5e1',
        darkSeriesFallback: '#cbd5e1',
      },
      stateOfCharge: {
        accent: '#17b88f',
        ringRemainder: '#7eddd0',
        icon: '#13b58d',
        greenEnergyDot: '#13b58d',
        gridEnergyDot: '#aee8de',
      },
      totalGreenFactor: {
        outerStart: '#f1e536',
        outerEnd: '#f59e0b',
        innerStart: '#8de4d1',
        innerEnd: '#0b7f62',
        usageColors: ['#e7d81a', '#0ea5e9', '#f97316', '#16a34a'],
        supplyColors: ['#8de4d1', '#0d9488', '#22c55e', '#86efac'],
      },
      solarEnergyGenerated: {
        liveDot: '#84cc16',
        liveDotRing: 'rgba(132, 204, 22, 0.2)',
      },
      batteryLevel: {
        filledSegments: [
          '#be123c',
          '#c5133f',
          '#d11d48',
          '#e11d48',
          '#ef4444',
          '#3b82f6',
          '#2563eb',
          '#1d4ed8',
          '#1e40af',
          '#0ea5e9',
          '#06b6d4',
          '#14b8a6',
          '#10b981',
          '#22c55e',
          '#4ade80',
          '#86efac',
        ],
        emptySegment: '#223045',
        percentText: '#2563eb',
      },
      powerUseVsGeneration: {
        electricityUse: '#8fb8ff',
        electricityGeneration: '#9ad9b1',
        axisLight: '#475569',
        axisDark: '#cbd5e1',
        gridLight: '#cbd5e1',
        gridDark: '#334155',
        tooltipLightBg: '#ffffff',
        tooltipDarkBg: '#111827',
        tooltipLightBorder: '#e5e7eb',
        tooltipDarkBorder: '#1f2937',
        tooltipLightText: '#111827',
        tooltipDarkText: '#e5e7eb',
      },
      solarPowerVsBatteryPower: {
        solarActive: '#67d57a',
        batteryActive: '#f5a6b4',
      },
      peakToday: {
        maxBatteryTemp: '#f87171',
        maxCellVolt: '#cbd5e1',
        peakSolarToday: '#86efac',
        peakLoadToday: '#93c5fd',
      },
    },
  },
};

export const FaithTheme = createTheme({
  palette: Palette,
  typography: {
    htmlFontSize: Typography.htmlFontSize,
    fontSize: Typography.fontSize,
    fontFamily: `${Typography.fontFamily} !important`,
    h1: {
      fontSize: '2.4rem',
      fontWeight: 500,
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: '-0.12px',
    },
    h3: {
      fontSize: '1.6rem',
      fontWeight: 600,
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: '-0.1px',
    },
    h6: {
      fontSize: '1.4rem',
      fontWeight: 600,
      lineHeight: 'normal',
      textTransform: 'uppercase',
    },
    body1: {
      fontSize: '1.6rem',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.5',
      letterSpacing: '-0.1px',
    },
    caption: {
      fontSize: '1.4rem',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: '-0.1px',
      color: Palette.text.primary,
    },
    subtitle1: {
      fontSize: '1.6rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    subtitle2: {
      fontSize: '1.4rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: '-0.08px',
      color: Palette.primary.main,
    },
    small: {
      fontSize: '1.4rem',
      fontWeight: Typography.fontWeight.regular,
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    fontWeightBold: Typography.fontWeight.bold,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      '2xl': 1920,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: Palette.background.body,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '2px',
          padding: '0',
        },
        containedPrimary: {
          height: 'auto',
          minHeight: '4rem',
          minWidth: '16rem',
          padding: '0',
          '@media (max-width: 600px)': {
            minWidth: '10rem',
            padding: '0 0.8rem',
          },
          '&:hover': {
            backgroundColor: Palette.primary.light,
          },
          '&:active': {
            backgroundColor: Palette.primary.dark,
          },
          '&:disabled': {
            opacity: 0.5,
            color: Palette.primary.contrastText,
            backgroundColor: Palette.primary.light,
          },
        },
        outlinedPrimary: {
          height: 'auto',
          minHeight: '4rem',
          minWidth: '16rem',
          padding: '0',
          '@media (max-width: 600px)': {
            minWidth: '10rem',
            padding: '0 0.8rem',
          },
          '&:focus': {
            backgroundColor: Palette.primary[200],
          },
          '&:hover': {
            backgroundColor: Palette.primary[200],
          },
          '&:active': {
            backgroundColor: Palette.primary.lighter,
          },
          '&:disabled': {
            opacity: 0.2,
          },
        },
        label: {
          padding: '0rem 0.8rem',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '1.2rem 1.6rem 1.6rem 1.6rem',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontSize: '1.6rem',
        },
        button: {
          color: Palette.primary.main,
          textDecoration: 'underline',
          '&:hover': {
            color: Palette.primary.light,
          },
          '&:active': {
            color: Palette.primary.dark,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: Palette.background.default,
          border: 0,
          boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.06)',
          marginBottom: '0.8rem',
          '&:not(:last-child)': {
            borderBottom: 0,
          },
          '&:before': {
            display: 'none',
          },
          [`&.${accordionClasses.expanded}`]: {
            margin: '0 0 0.8rem 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          marginBottom: -1,
          minHeight: '4.8rem',
          [`&.${accordionSummaryClasses.expanded}`]: {
            minHeight: '4.8rem',
          },
          border: 0,
          padding: '0 1.6rem 0 1.2rem',
        },
        content: {
          [`&.${accordionSummaryClasses.expanded}`]: {
            margin: '1.2rem 0',
            border: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '0.8rem 1.4rem 1.6rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&:not(.${outlinedInputClasses.multiline})`]: {
            [`&:hover:not(.${outlinedInputClasses.disabled}):not(.${outlinedInputClasses.focused}):not(.${outlinedInputClasses.error}) .${outlinedInputClasses.notchedOutline}`]:
              {
                borderColor: theme.palette.border.hover,
              },
            [`&.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.border.disabled,
            },
          },
        }),
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.border.main,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '2px',
        }),
        adornedStart: {
          paddingLeft: '0.48rem',
        },
        input: {
          padding: '1.05rem 1.4rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.muted,
          [`&.${inputClasses.disabled}`]: {
            backgroundColor: theme.palette.background.disabled,
          },
        }),
        input: ({ theme }) => ({
          padding: '0 1.2rem 0 0 ',
          color: theme.palette.text.primary,
          fontSize: '1.4rem',
          '&::placeholder': {
            color: theme.palette.text.muted,
            opacity: 1,
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: Typography.fontFamily,
          fontSize: '1.4rem',
          fontWeight: Typography.fontWeight.regular,
          fontStretch: 'normal',
          fontStyle: 'normal',
          lineHeight: 1.14,
          letterSpacing: 'normal',
        },
        contained: {
          marginTop: '0.125',
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          height: '4rem',
          minWidth: ' 0.125rem',
          color: Palette.primary.lighter,
        },
        fixed: {
          height: '4rem',
          color: Palette.primary.lighter,
        },
        indicator: {
          backgroundColor: Palette.primary.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: Palette.primary.lighter,
          [`&.${tabClasses.selected}`]: {
            color: Palette.primary.main,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          // TODO: The SVGs used in many (all?) of the toggle buttons do not inherit the text color.
          //  Ideally, the toggle button bg should be background.muted (or background.body?) with a text.main text color,
          //  but that is currently not possible when the icons are hard-coded to be white.
          color: `${Colors.white} !important`,
          border: 0,
          padding: '0',
          width: '4.66rem',
          height: '4rem',
          fontSize: '1.4rem',
          borderRadius: 2,
          backgroundColor: `${Palette.neutral[500]} !important`,
          '&:hover': {
            backgroundColor: Palette.neutral[500],
          },
          [`&.${toggleButtonClasses.selected}`]: {
            color: Palette.primary.contrastText,
            backgroundColor: `${Palette.primary.main} !important`,
            '&:hover': {
              backgroundColor: Palette.primary.main,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paperWidthSm: {
          width: '62.4rem',
          maxWidth: '62.4rem',
          '@media (max-width: 600px)': {
            width: 'calc(100vw - 3.2rem)',
            maxWidth: 'calc(100vw - 3.2rem)',
          },
        },
        paper: {
          margin: 0,
          '@media (max-width: 600px)': {
            margin: '1.6rem',
            maxHeight: 'calc(100dvh - 3.2rem)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '2.4rem 3.2rem 1.6rem',
          fontFamily: `${Typography.fontFamily} !important`,
          fontSize: '2.4rem',
          fontWeight: Typography.fontWeight.bold,
          fontStretch: 'normal',
          fontStyle: 'normal',
          lineHeight: 'normal',
          letterSpacing: '-0.12px',
          color: Palette.text.primary,
          borderBottom: '1px solid',
          borderBottomColor: Palette.border.main,
          '@media (max-width: 600px)': {
            padding: '1.6rem',
            fontSize: '2rem',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontFamily: `${Typography.fontFamily} !important`,
          padding: '1.6rem 3.2rem',
          fontSize: '1.4rem',
          fontWeight: Typography.fontWeight.regular,
          fontStretch: 'normal',
          fontStyle: 'normal',
          lineHeight: 'normal',
          letterSpacing: '-0.08px',
          color: Palette.text.primary,
          minHeight: '8rem',

          [`.${dialogTitleClasses.root} + &`]: {
            paddingTop: '1.6rem', // Match the vertical padding used above.
          },
          '@media (max-width: 600px)': {
            padding: '1.6rem',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '2.4rem 3.2rem',
          fontFamily: `${Typography.fontFamily} !important`,
          flexWrap: 'wrap',
          '@media (max-width: 600px)': {
            padding: '1.6rem',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: Palette.border.disabled, // Should this be changed to border.main?
          [`&.${outlinedInputClasses.checked}`]: {
            color: Palette.primary.main,
          },
        },
      },
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: {
          width: '2rem',
          height: '2rem',
          fontSize: '1rem',
          fontWeight: Typography.fontWeight.bold,
          letterSpacing: '-0.06px',
          textAlign: 'center',
          backgroundColor: Palette.primary.main,
          marginLeft: '4px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: ({ theme }) => ({
          [`&.${selectClasses.outlined}`]: {
            paddingRight: '5.3rem',
          },
          [`&.${selectClasses.disabled}`]: {
            backgroundColor: theme.palette.background.disabled,
          },
        }),
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: {
          minWidth: '2rem',
          paddingRight: '1.2rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: ({ theme }) => ({
          [`&.${inputLabelClasses.outlined}`]: {
            fontSize: '1.4rem',
            color: theme.palette.text.muted,
            transform: 'translate(1.4rem, 1.2rem) scale(1) !important',
            [`&.${inputLabelClasses.shrink}`]: {
              transform: 'translate(1.4rem, -6px) scale(0.75) !important',
            },
          },
        }),
      },
    },
  },
  props: {
    MuiButton: {
      disableRipple: true,
      disableElevation: true,
    },
    MuiCheckbox: {
      disableRipple: true,
    },
  },
});

export const FaithThemeDark = createTheme(FaithTheme, {
  palette: {
    ...FaithTheme.palette,
    mode: 'dark',
    text: {
      ...FaithTheme.palette.text,
      primary: '#e5e7eb',
      secondary: '#b8c5d6',
      muted: '#b8c5d6',
      disabled: 'rgba(255, 255, 255, 0.38)',
    },
    border: {
      ...FaithTheme.palette.border,
      main: '#334155',
      disabled: '#475569',
      hover: '#64748b',
    },
    divider: '#334155',
    background: {
      ...FaithTheme.palette.background,
      default: '#0f172a',
      paper: '#111827',
      body: '#0b1220',
      muted: '#1f2937',
      mutedHover: '#334155',
      disabled: '#374151',
    },
  },
  components: {
    ...FaithTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0b1220',
          color: '#e5e7eb',
        },
      },
    },
  },
});
